import { createSelector } from '@ngrx/store'
import { tableLightColors, publishDotMenuIds } from 'src/app/helpers/table-helpers'
import { capitalize } from '../../helpers/helpers'
import { PublishTableData } from 'src/app/helpers/table-column-types'
import { JoiEvent } from 'globals/store/events.data'
import { BrandingSelectData, LogisticsInBriefSelection, PublishInBriefSelection, getBrandingData } from 'globals/store/publish.data'
import { eventListLightsByPublishIdsReselector } from './event-lists.reselector'
import { getBriefLogisticsViews, getBriefOnsiteViews, getBriefProgramPublishes } from '../share.brief.functions'
import { CoreEntityType, LogisticsShareable, ShareOSSInfo } from 'globals/store/share.data';
import { PublishInfo } from 'globals/store/share.data';
import { shareStateSelector } from '../share.era'
import { eventStateSelector } from '../events.era'
import { filterStateSelector } from '../filters.era'
import { accountStateSelector } from '../accounts.era'
import { appsStateSelector } from '../app.era'
import { makeMapFromListOfObjectsAndKey, sortByProp } from 'globals/helpers/helpers'

const publishInfos = createSelector(shareStateSelector, (store) => store ? store.publish || {} : {})
const logisticsShares = createSelector(shareStateSelector, (store) => store ? store.logistics || {} : {})
const onsiteShares = createSelector(shareStateSelector, (store) => store ? store.oss || {} : {})
const eventTitle = createSelector(eventStateSelector, (store) => store.event ? store.event.title : 'Unknown Title')
const publishSearchTerm = createSelector(filterStateSelector, (store) => store.publish.search)
const event = createSelector(eventStateSelector, (store) => store.event)
const account = createSelector(accountStateSelector, (store) => store.account)
const accounts = createSelector(accountStateSelector, (store) => store.entities)
const apps = createSelector(appsStateSelector, (store) => store.entities)

export const brandDataReselector = createSelector(
  account,
  accounts,
  event,
  publishInfos,
  apps,
  (acc, accounts, event, publishInfos, apps, brandingSelectData: BrandingSelectData) => getBrandingData({ acc, accounts, event, apps, publishInfos, brandingSelectData })
)

export const performerBriefProgramPublishSelectionsReselector = createSelector(
  publishInfos,
  event,
  (publishInfos: { [id:string]: PublishInfo }, event: JoiEvent): PublishInBriefSelection[] => 
    Object.values(publishInfos).map(({ publishCode, title, status }) => ({ publishCode, title, status, on: status === 'published' && event?.publishIdsToShowInPerformerBrief.includes(publishCode) })).sort(sortByProp('title'))
)

export const exhibitorBriefProgramPublishSelectionsReselector = createSelector(
  publishInfos,
  event,
  (publishInfos: { [id:string]: PublishInfo }, event: JoiEvent): PublishInBriefSelection[] => 
    Object.values(publishInfos).map(({ publishCode, title, status }) => ({ publishCode, title, status, on: status === 'published' && event?.publishIdsToShowInExhibitorBrief.includes(publishCode) })).sort(sortByProp('title'))
)

export const resourceBriefProgramPublishSelectionsReselector = createSelector(
  publishInfos,
  event,
  (publishInfos: { [id:string]: PublishInfo }, event: JoiEvent): PublishInBriefSelection[] => 
    Object.values(publishInfos).map(({ publishCode, title, status }) => ({ publishCode, title, status, on: status === 'published' && event?.publishIdsToShowInResourceBrief.includes(publishCode) })).sort(sortByProp('title'))
)

export const attendeeBriefProgramPublishSelectionsReselector = createSelector(
  publishInfos,
  event,
  (publishInfos: { [id:string]: PublishInfo }, event: JoiEvent): PublishInBriefSelection[] => 
    Object.values(publishInfos).map(({ publishCode, title, status }) => ({ publishCode, title, status, on: status === 'published' && event?.publishIdsToShowInAttendeeBrief.includes(publishCode) })).sort(sortByProp('title'))
)

export const briefProgramPublishesReselector = createSelector(
  publishInfos,
  event,
  (publishInfos: { [id:string]: PublishInfo}, event: JoiEvent, briefType: CoreEntityType) => 
    getBriefProgramPublishes(publishInfos, briefType === 'performer' ? event?.publishIdsToShowInPerformerBrief : briefType === 'exhibitor' ? event?.publishIdsToShowInExhibitorBrief : event?.publishIdsToShowInResourceBrief, briefType, event)
)

export const briefLogisticsSelectionsReselector = createSelector(
  logisticsShares,
  event,
  (logisticsShares: { [id:string]: LogisticsShareable }, event: JoiEvent, idsKey: keyof Pick<JoiEvent, 'pBriefLogIds' | 'eBriefLogIds' | 'rBriefLogIds'>): LogisticsInBriefSelection[] => 
    Object.values(logisticsShares).map(({ code, title, status }) => ({ code, title, status, on: status === 'shared' && event && event[idsKey]?.includes(code) })).sort(sortByProp('title'))
)


export const briefLogisticsReselector = createSelector(
  logisticsShares,
  event,
  (logisticsShares: { [id:string]: LogisticsShareable }, event: JoiEvent, briefType: CoreEntityType) => 
    getBriefLogisticsViews(logisticsShares, briefType === 'performer' ? event?.pBriefLogIds || [] : briefType === 'exhibitor' ? event?.eBriefLogIds || [] : event?.rBriefLogIds || [], briefType, event)
)


export const briefSharesSelectionsReselector = createSelector(
  onsiteShares,
  event,
  (onsiteShares: { [id:string]: ShareOSSInfo }, event: JoiEvent, idsKey: keyof Pick<JoiEvent, 'pShareLogIds' | 'eShareLogIds' | 'rShareLogIds' | 'aShareLogIds'>): LogisticsInBriefSelection[] => {
    // console.log({ onsiteShares, event, idsKey })
    return Object.values(onsiteShares).map(({ id, status, title }) => ({ code: id, status, title, on: status === 'shared' && event[idsKey].includes(id) }))
  }
)

export const briefSharesReselector = createSelector(
  onsiteShares,
  event,
  (onsiteShares: { [id:string]: ShareOSSInfo }, event: JoiEvent, briefType: CoreEntityType) => 
    getBriefOnsiteViews(Object.values(onsiteShares), briefType === 'performer' ? event?.pShareLogIds || [] : briefType === 'exhibitor' ? event?.eShareLogIds || [] : event?.rShareLogIds || [], briefType, event)
)


export const publishInfoReselector = createSelector(
  publishInfos,
  (publishInfoMap: { [id:string]: PublishInfo }, publishCode: string) => {
    // console.log({ publishInfo, eventTitle, publishCode })
    if (!publishInfoMap || !publishCode) return null
    const publishInfo = publishInfoMap[publishCode]
    return publishInfo
  }
)

export const publishReselector = createSelector(
  publishInfos,
  eventTitle,
  event,
  publishSearchTerm,
  eventListLightsByPublishIdsReselector,
  (publishInfo, eventTitle, event, searchTerm, publishedIdToEventList) => {
    const publishCodes = Object.keys(publishInfo)
    searchTerm = searchTerm ? searchTerm.toLocaleUpperCase() : null

    const publishCountBeforeFiltering = publishCodes.length
    const publishTableData: PublishTableData[] = publishCodes.filter(code => {
      const { title, publisher } = publishInfo[code]
      return !searchTerm || title.toLocaleUpperCase().includes(searchTerm) ||
            (publisher && publisher.toLocaleUpperCase().includes(searchTerm))
    }).map(code => {
      const { publishedAt, publisher, status, title, description } = publishInfo[code]
      const activeDotMenuOptions = new Set(publishDotMenuIds)
      if (status !== 'published') activeDotMenuOptions.delete('update')
      const eventListLightsInfo = publishedIdToEventList[code]

      const publishTableRow: PublishTableData = {
        id: code,
        name: { text: title || eventTitle},
        description: { text: description || '' },
        status: {
          percent: status === 'published' ? 1 : status === 'unpublished' ? 0 : -1,
          lights: [{
            text: !event || event.closed ? 'Unpublished' : capitalize(status),
            color: !event || event.closed ? tableLightColors.gray : status === 'published' ? tableLightColors.green  : status === 'unpublished' ? tableLightColors.red  : tableLightColors.gray,
          }]},
        updated: { updater: publisher, lastUpdated: publishedAt },
        eventLists: status !== 'published' || !eventListLightsInfo ? null : { 
          percent: !eventListLightsInfo.length ? 0 : eventListLightsInfo.filter(i => i.published).length / eventListLightsInfo.length, 
          lights: eventListLightsInfo.map((info, index) => ({ 
            text: `${info.name}${index < eventListLightsInfo.length - 1 ? ', ' : ''}`, 
            color: info.published ? tableLightColors.green : tableLightColors.gray 
          }))
        },
        publishInfo: publishInfo[code],
        activeDotMenuOptions
      }
      return publishTableRow
    })
    const publishTableRowsById = makeMapFromListOfObjectsAndKey(publishTableData, 'id')
    return { publishTableData, publishTableRowsById, publishCountBeforeFiltering }
  }
)
